import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Layers, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useI18n } from "@/context/I18nContext";
import AssetPlayer from "@/components/assets/AssetPlayer";
import ExportMenu from "@/components/assets/ExportMenu";

export function ClassAssetList({ classId }) {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/classes/${classId}/asset-assignments`).then((r) => setRows(r.data.items)).catch(() => {}); }, [classId]);
  if (!rows.length) return null;
  return (
    <section className="mt-8" data-testid="class-asset-list">
      <h2 className="font-cabinet font-extrabold text-2xl flex items-center gap-2"><Layers className="w-6 h-6" /> {t("al.classResources")}</h2>
      <div className="space-y-2 mt-3">
        {rows.map((r) => (
          <Link key={r.asset_assignment_id} to={`/my-classes/${classId}/resources/${r.asset_assignment_id}`} className="nb-card p-3 flex items-center justify-between gap-3" data-testid={`class-asset-${r.asset_assignment_id}`}>
            <span className="font-bold" dir="auto">{r.title}</span>
            <span className="text-xs text-gray-600 flex items-center gap-1">{r.cefr} · {t(`al.fam.${r.family}`)} {r.completed && <Check className="w-4 h-4" />}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function AssetAssignment() {
  const { t } = useI18n();
  const { classId, aid } = useParams();
  const [d, setD] = useState(null);
  const [res, setRes] = useState(null);
  useEffect(() => {
    api.get(`/asset-assignments/${aid}`).then((r) => {
      setD(r.data);
      if (r.data.is_teacher) api.get(`/asset-assignments/${aid}/results`).then((x) => setRes(x.data)).catch(() => setRes({ error: true }));
    }).catch(() => setD(false));
  }, [aid]);
  if (d === false) return <AppLayout><p className="max-w-3xl mx-auto px-4 py-8 text-coral" data-testid="asset-assignment-denied">{t("edu.cls.noAccess")}</p></AppLayout>;
  if (!d) return <AppLayout><div className="max-w-3xl mx-auto px-4 py-8"><div className="nb-card h-40 animate-pulse" /></div></AppLayout>;
  const a = d.assignment;
  const practice = async (results) => {
    try { await api.post(`/asset-assignments/${aid}/practice`, { results }); toast.success(t("al.practiceDone")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("al.failed")); }
  };
  const keep = async () => {
    try { await api.post(`/asset-assignments/${aid}/keep`, {}); return true; }
    catch { toast.error(t("al.failed")); return false; }
  };
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="asset-assignment">
        <Link to={d.is_teacher ? `/class/${classId}` : `/my-classes/${classId}`} className="text-sm underline" data-testid="asset-back"><span className="inline-block rtl:rotate-180">←</span> {t("missionRun.backToClass")}</Link>
        <h1 className="font-cabinet font-extrabold text-3xl mt-3" dir="auto" data-testid="asset-assignment-title">{a.title}</h1>
        <p className="text-sm text-gray-600 mt-1" dir="auto">{a.student_instructions}</p>
        <div className="nb-card p-5 mt-5">
          <AssetPlayer family={a.family} language={a.language} content={a.content} answers={d.is_teacher ? a.answers : null}
            onPractice={d.is_teacher ? null : practice} keep={d.is_teacher ? null : d.keep} onKeep={keep} initialDone={!!d.my_practice?.completed} />
        </div>
        <div className="mt-4"><ExportMenu base={`/asset-assignments/${aid}/export`} teacher={d.is_teacher} name={a.title} /></div>
        {res?.error && <p className="text-coral text-sm mt-4" data-testid="asset-results-error">{t("al.failed")}</p>}
        {res && !res.error && (
          <div className="nb-card p-4 mt-5" data-testid="asset-results">
            <div className="font-bold">{t("al.results")}: {res.completed}/{res.total}</div>
            <ul className="text-sm mt-2 space-y-1">{res.students.map((s) => (
              <li key={s.user_id} className="flex justify-between" data-testid={`asset-result-${s.user_id}`}><span>{s.name}</span>
                <span>{s.completed ? t("al.completed") : s.attempted ? t("al.attempted") : t("al.notAttempted")}</span></li>
            ))}</ul>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
